import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { ZoomDialog } from './zoom-dialog.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
/** Mobile double-tap zoom level */
const STEP_ZOOM = 2.2;
/** Desktop click zoom level (single step: fit ↔ zoomed) */
const DESKTOP_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;
/** Clicks shorter than this (ms) feel intentional; avoids counting long press as zoom. */
const MAX_ZOOM_CLICK_MS = 400;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {boolean} */
  #desktopInitialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** Desktop: 0 = fit, 1..N = successive click-zoom tiers (cycles). */
  #zoomTier = 0;

  /** Desktop pointer pan */
  /** @type {number | null} */
  #activePointerId = null;
  /** Primary pointer id for distinguishing multi-pointer (always set on desktop pointerdown). */
  /** @type {number | null} */
  #gesturePointerId = null;
  #panCandidate = false;
  #didPanOrScroll = false;
  /** @type {{ x: number; y: number } | null} */
  #pointerDownPos = null;
  #pointerDownTime = 0;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();
    this.#setupDialogCloseListener();

    if (isMobileBreakpoint()) {
      this.#initEventListeners();
    } else {
      this.#initDesktopEventListeners();
    }

    this.#updateTransform();
  }

  #initDesktopEventListeners() {
    if (this.#desktopInitialized) return;
    this.#desktopInitialized = true;

    const { signal } = this.#controller;
    const opts = { passive: false, signal };

    this.addEventListener('pointerdown', this.#handlePointerDown, opts);
    this.addEventListener('pointermove', this.#handlePointerMove, opts);
    this.addEventListener('pointerup', this.#handlePointerUp, opts);
    this.addEventListener('pointercancel', this.#handlePointerCancel, opts);
    this.addEventListener('wheel', this.#handleWheel, opts);
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  /**
   * Override parent zoom dialog's close method to include reset functionality
   */
  #setupDialogCloseListener() {
    // Find the parent zoom dialog component
    const zoomDialog = /** @type {ZoomDialog} */ (this.closest('zoom-dialog'));
    if (!zoomDialog || typeof zoomDialog.close !== 'function') return;

    // Store reference to original close method
    const originalClose = zoomDialog.close.bind(zoomDialog);

    // Override the close method to include zoom reset
    zoomDialog.close = async (...args) => {
      // Reset zoom state before closing
      this.#resetZoom();

      // Call original close method
      return await originalClose(...args);
    };
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  /**
   * @param {PointerEvent} event
   * @returns {boolean}
   */
  #isDesktopFinePointer(event) {
    if (isMobileBreakpoint()) return false;
    if (event.pointerType === 'touch') return false;
    return true;
  }

  /**
   * Toggle desktop zoom: fit ↔ zoomed (centered zoom, no offset issues).
   */
  #toggleDesktopZoom() {
    this.classList.add('drag-zoom-wrapper--animate-zoom');

    const isZoomed = this.#scale > MIN_ZOOM + 0.02;

    if (isZoomed) {
      // Zoom out to fit
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#zoomTier = 0;
    } else {
      // Zoom in centered
      this.#scale = DESKTOP_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#zoomTier = 1;
    }

    this.#requestUpdateTransform();
    window.setTimeout(() => this.classList.remove('drag-zoom-wrapper--animate-zoom'), 220);
  }

  #handlePointerDown = (event) => {
    if (!this.#isDesktopFinePointer(event) || event.button !== 0) return;

    event.preventDefault();

    this.#gesturePointerId = event.pointerId;
    this.#pointerDownPos = { x: event.clientX, y: event.clientY };
    this.#pointerDownTime = performance.now();
    this.#didPanOrScroll = false;
    this.#hasDraggedBeyondThreshold = false;

    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }

    const isZoomed = this.#scale > MIN_ZOOM + 0.02;
    this.#activePointerId = event.pointerId;
    this.#startPosition = { x: event.clientX, y: event.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };

    if (isZoomed) {
      this.#panCandidate = true;
      this.classList.add('drag-zoom-wrapper--grabbing');
      this.setAttribute('data-dragging', '');
    } else {
      this.#panCandidate = false;
    }
  };

  #handlePointerMove = (event) => {
    if (!this.#isDesktopFinePointer(event)) return;
    if (this.#activePointerId !== event.pointerId) return;

    event.preventDefault();

    const isZoomed = this.#scale > MIN_ZOOM + 0.02;
    if (!isZoomed) return;

    const dx = event.clientX - this.#startPosition.x;
    const dy = event.clientY - this.#startPosition.y;
    const distance = Math.hypot(dx, dy);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;
    this.#didPanOrScroll = true;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  };

  #handlePointerUp = (event) => {
    if (!this.#isDesktopFinePointer(event)) return;
    if (this.#activePointerId !== event.pointerId) return;

    event.preventDefault();

    try {
      this.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }

    this.classList.remove('drag-zoom-wrapper--grabbing');
    this.removeAttribute('data-dragging');

    const duration = performance.now() - this.#pointerDownTime;
    let moved = 0;
    if (this.#pointerDownPos) {
      moved = Math.hypot(event.clientX - this.#pointerDownPos.x, event.clientY - this.#pointerDownPos.y);
    }

    const isClick = moved < DRAG_THRESHOLD && !this.#didPanOrScroll && duration < MAX_ZOOM_CLICK_MS;

    if (isClick) {
      this.#toggleDesktopZoom();
    }

    this.#activePointerId = null;
    this.#gesturePointerId = null;
    this.#panCandidate = false;
    this.#pointerDownPos = null;
    this.#hasDraggedBeyondThreshold = false;
  };

  #handlePointerCancel = (event) => {
    try {
      this.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    this.#gesturePointerId = null;
    this.#pointerDownPos = null;
    this.#activePointerId = null;
    this.#panCandidate = false;
    this.classList.remove('drag-zoom-wrapper--grabbing');
    this.#hasDraggedBeyondThreshold = false;
    this.#didPanOrScroll = false;
  };

  /**
   * When zoomed, wheel/trackpad pans the image vertically (deltaY) or horizontally (Shift+delta).
   */
  #handleWheel = (event) => {
    if (isMobileBreakpoint() || this.#scale <= MIN_ZOOM + 0.02) return;

    let dx = event.deltaX;
    let dy = event.deltaY;

    if (event.deltaMode === 1) {
      dx *= 16;
      dy *= 16;
    } else if (event.deltaMode === 2) {
      dx *= window.innerWidth || 1024;
      dy *= window.innerHeight || 768;
    }

    this.#didPanOrScroll = true;
    preventDefault(event);

    const s = this.#scale;
    this.#translate.x -= dx / s;
    this.#translate.y -= dy / s;

    this.#requestUpdateTransform();
  };

  #handleResize = () => {
    if (isMobileBreakpoint()) {
      if (!this.#initialized) {
        this.#initEventListeners();
      }
    } else {
      if (!this.#desktopInitialized) {
        this.#initDesktopEventListeners();
      }
    }

    this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        // Distance calculation with early exit
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
    this.setAttribute('data-dragging', '');
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch - simple centered zoom like desktop.
   * @param {Touch} _touch - The touch where the double-tap occurred (unused, zoom is centered)
   */
  #handleDoubleTapFromTouch(_touch) {
    const isZoomed = this.#scale > MIN_ZOOM + 0.05;

    // Reset manual zoom flag
    if (this.#hasManualZoom) {
      this.#hasManualZoom = false;
    }

    if (isZoomed) {
      // Zoom out to fit - centered
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
    } else {
      // Zoom in - centered
      this.#scale = STEP_ZOOM;
      this.#translate = { x: 0, y: 0 };
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Mark that manual zoom has been used
    this.#hasManualZoom = true;

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    // Calculate how the image position needs to change to keep the midpoint stationary
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
    this.removeAttribute('data-dragging');
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.removeAttribute('data-dragging');
      this.#requestUpdateTransform();

      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * Constrain image translation - allows free canvas-style dragging when zoomed.
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), center the image with no dragging
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // When zoomed: allow free canvas-style panning in all directions
    // Only limit to prevent dragging the image completely out of view
    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;

    let naturalWidth = imageElement.naturalWidth || wrapperRect.width;
    let naturalHeight = imageElement.naturalHeight || wrapperRect.height;

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    // Scaled image dimensions
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // Free drag: allow panning until only 20% of the image remains visible
    // This gives canvas-like freedom while keeping some image always in view
    const minVisibleRatio = 0.2;
    const minVisibleX = scaledImageWidth * minVisibleRatio;
    const minVisibleY = scaledImageHeight * minVisibleRatio;

    // Max translation allows dragging until only minVisible portion stays in viewport
    const maxTranslateX = (scaledImageWidth - minVisibleX) / 2 / this.#scale;
    const maxTranslateY = (scaledImageHeight - minVisibleY) / 2 / this.#scale;

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);

    const isZoomed = this.#scale > MIN_ZOOM + 0.02;
    this.classList.toggle('drag-zoom-wrapper--zoomed', isZoomed);
    if (isZoomed) {
      this.setAttribute('data-zoomed', '');
    } else {
      this.removeAttribute('data-zoomed');
    }
  };

  /**
   * Reset zoom to fit (1×), centered. Called when the zoom dialog closes.
   */
  #resetZoom() {
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#zoomTier = 0;

    this.style.setProperty('--drag-zoom-scale', MIN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');

    // Update zoomed attribute
    this.removeAttribute('data-zoomed');
    this.removeAttribute('data-dragging');
    this.classList.remove('drag-zoom-wrapper--zoomed');
  }

  /**
   * Public method to reset zoom (called by external components like certificate slider)
   */
  resetZoom() {
    this.#resetZoom();
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
